// Exact port of OG's `SGFNT` worksheet function =
// ReportMultilanguage.LIMSFormula.FormatSF(value, sigFig, /*bolIsNotation*/ true)
// (legacy VB: DynamicReportDesigner/.../ML/LIMSFormula.vb), used by 4 SDMS templates.
//
// This is a VERBATIM port — it reproduces OG's behaviour exactly, INCLUDING its quirks (the
// negative-number notation emits an extra digit; certain round-carry cases), so SDMS matches
// legacy 1:1. Proven BYTE-IDENTICAL to the real compiled DLL across a 236-case golden battery
// (positives/negatives, |x|<1 and >=1, notation/non-notation, ties, carries, sig-fig 1-4) —
// see sgfntFormat.test.ts. Do NOT "clean up" the odd branches; they are load-bearing for parity.
//
// Culture is en-US ("." decimal separator), matching the lab. The one non-VB addition is a G15
// step in `cstr` (net-Framework `CStr(Double)` renders 15 significant figures, which strips the
// binary-float noise a raw JS `Number.toString()` would keep, e.g. 2.3000000000000003 -> "2.3");
// same compatibility shim the E_RoundOff reuse needed.

const SUPERSCRIPT: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
  '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻', '+': '⁺',
};
const superScript = (s: string): string => [...String(s)].map(c => SUPERSCRIPT[c] ?? '').join('');

// VB Math.Round (banker's, round-half-to-even) to an integer.
function vbRoundEven(x: number): number {
  if (!Number.isFinite(x)) return x;
  const s = x < 0 ? -1 : 1, ax = Math.abs(x), fl = Math.floor(ax), d = ax - fl, E = 1e-9;
  let r: number;
  if (Math.abs(d - 0.5) < E) r = (fl % 2 === 0) ? fl : fl + 1;
  else r = Math.round(ax);
  return s * r;
}

// VB `CStr(Double)` — net-Framework "G15": 15 significant figures, trailing zeros trimmed
// (this is what strips the float noise; see the header note).
function cstr(x: number | string): string {
  if (typeof x !== 'number') return String(x);
  if (Object.is(x, -0)) x = 0;
  if (!Number.isFinite(x)) return x.toString();
  const cleaned = Number(x.toPrecision(15));
  let s = cleaned.toString();
  if (s.includes('e-')) { s = cleaned.toFixed(20); if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, ''); }
  else if (s.includes('e+')) { s = cleaned.toFixed(0); }
  return s;
}

// VB Mid (1-based); omitted length = to end.
function mid(s: string, start: number, len?: number): string {
  s = String(s);
  if (start < 1) start = 1;
  if (len === undefined) return s.substring(start - 1);
  if (start > s.length) return '';
  return s.substr(start - 1, len);
}
const vbLeft = (s: string, n: number): string => String(s).substring(0, Math.max(0, n));
const vbRight = (s: string, n: number): string => (n <= 0 ? '' : String(s).substring(Math.max(0, String(s).length - n)));
const vbLen = (s: string): number => String(s).length;
function vbVal(s: string): number { const m = String(s).match(/^\s*[-+]?(\d+\.?\d*|\.\d+)/); return m ? parseFloat(m[0]) : 0; }
const instr = (s: string, sub: string): number => String(s).indexOf(sub) + 1;   // 1-based; 0 if absent
function cdbl(s: string | number): number { const n = parseFloat(String(s)); return Number.isNaN(n) ? 0 : n; }
// .NET (net-Framework) String.Format("{0:F<n>}") uses banker's rounding.
const formatF = (x: number, n: number): string => (vbRoundEven(x * 10 ** n) / 10 ** n).toFixed(Math.max(0, n));
function num(ch: string): number { const n = Number(ch); return Number.isNaN(n) ? 0 : n; }
function getNoOfFractionDigits(dbl: number): number { const p = cstr(dbl).split('.'); return p.length > 1 ? (p[1] ?? '').length : 0; }
// Int(Log10(Abs(x))) via exact digit counting — matches the oracle and avoids the JS `Math.log10`
// float-boundary bug (log10(1000) = 2.999… -> floor 2, but OG gives exponent 3).
function intCorrPowerOf(x: number): number {
  const ax = Math.abs(x);
  if (ax === 0) return 0;
  const s = cstr(ax);
  if (ax >= 1) return (s.split('.')[0] ?? '').length - 1;
  const frac = s.split('.')[1] ?? '';
  let z = 0; while (z < frac.length && frac[z] === '0') z++;
  return -(z + 1);
}

/** SGFNT = OG `FormatSF(value, sigFig, true)`. Returns the significant-figure string, in
 *  `m.mm X 10 <superscript-exponent>` scientific notation when the base-10 exponent ≠ 0. */
export function formatSF(dblInput: number, intSF: number): string {
  try {
    if (dblInput === 0) return cstr(dblInput);
    let strResult = '', strCal = '';
    let intInputDigitCount = 0;
    const strInput = cstr(dblInput);
    if (strInput && vbLen(strInput) > 0) {
      const head = strInput.split('.')[0] ?? '';
      intInputDigitCount = head.includes('-') ? vbLen(head) - 1 : vbLen(head);
    }
    const intCorrPower = intCorrPowerOf(dblInput);
    strCal = cstr(dblInput);

    if (intCorrPower >= 0 && mid(strCal, 1, intSF).includes('.')) {
      try {
        const chArrCal = strInput;
        const strTemp = strInput.replace(/-/g, '').replace(/\./g, '');
        if (strTemp.length > intSF && num(mid(strTemp, intSF + 1, 1)) > 4) {
          let intExtra = 0; const first = vbLeft(chArrCal, intSF);
          if (first.includes('-') && first.includes('.')) intExtra = 2;
          else if (first.includes('-') || first.includes('.')) intExtra = 1;
          if (num(mid(strTemp, intSF, 1)) % 2 === 0 && num(mid(strTemp, intSF + 1, 1)) === 5) {
            if (strTemp.length === intSF + 1) strCal = vbLeft(chArrCal, intSF + intExtra);
          } else {
            strCal = vbLeft(chArrCal, intSF + intExtra);
            if (vbVal(vbRight(strCal, 1)) > 8 && strCal.includes('.')) {
              const nd = strCal.length - instr(strCal, '.'); let sd = '0.'; while (sd.length < nd + 1) sd += '0';
              strCal = cstr(cdbl(strCal) + cdbl(sd + '1'));
            } else if (vbVal(vbRight(strCal, 1)) > 8) { strCal = cstr(vbVal(strCal) + 1); }
            else { strCal = vbLeft(chArrCal, intSF + intExtra - 1) + String(num(vbRight(strCal, 1)) + 1); }
          }
        }
      } catch { strCal = strInput; } finally { dblInput = cdbl(strCal); }
    } else if (mid(strCal, 1, intSF).includes('.')) {
      try {
        const chArrCal = strInput;
        const strTemp = strInput.replace(/-/g, '').replace(/\./g, '');
        const intZeroCount = strTemp.length - strTemp.replace(/^0+/, '').length;
        if ((intZeroCount + intSF + 1) >= 0 && (intZeroCount + intSF + 1) <= strTemp.length) {
          if (strTemp.length > intSF && num(mid(strTemp, intZeroCount + intSF + 1, 1)) > 4) {
            let intExtra = 0; const first = vbLeft(chArrCal, intSF);
            if (first.includes('-') && first.includes('.')) intExtra = 2 + intZeroCount;
            else if (first.includes('-') || first.includes('.')) intExtra = 1 + intZeroCount;
            if (num(mid(strTemp, intSF + intZeroCount, 1)) % 2 === 0 && num(mid(strTemp, intSF + 1 + intZeroCount, 1)) === 5 &&
              (mid(strTemp, intSF + 2 + intZeroCount, 1) === '' || num(mid(strTemp, intSF + 2 + intZeroCount, 1)) === 0)) {
              strCal = vbLeft(chArrCal, intSF + intExtra);
            } else {
              strCal = vbLeft(chArrCal, intSF + intExtra);
              if (vbVal(vbRight(strCal, 1)) > 8 && strCal.includes('.')) {
                const nd = strCal.length - instr(strCal, '.'); let sd = '0.'; while (sd.length < nd + 1) sd += '0';
                strCal = cstr(cdbl(strCal) + cdbl(sd + '1'));
              } else if (vbVal(vbRight(strCal, 1)) > 8) { strCal = cstr(vbVal(strCal) + 1); }
              else { strCal = vbLeft(chArrCal, intSF + intExtra - 1) + String(num(vbRight(strCal, 1)) + 1); }
            }
          }
        }
      } catch { strCal = strInput; } finally { dblInput = cdbl(strCal); }
    }

    strCal = cstr(vbRoundEven(dblInput * 10 ** ((intSF - 1) - intCorrPower)));

    if (intCorrPower > 0) {
      if (!(instr(cstr(dblInput), '.') === 0 && vbLen(strCal.replace(/-/g, '')) === intSF)) {
        let strDot = '';
        if ((intSF - (intInputDigitCount + 1)) >= 0 && (intSF - (intInputDigitCount + 1)) <= strCal.length) {
          if (cdbl(mid(strCal, strCal.length - (intSF - (intInputDigitCount + 1)), strCal.length)) === 0)
            strDot = mid(strCal, strCal.length - (intSF - (intInputDigitCount + 1)), strCal.length);
        }
        if (intCorrPower !== intSF) strCal = cstr(cdbl(strCal) * 10 ** (intCorrPower - (intSF - 1)));
        strCal = formatF(cdbl(strCal), getNoOfFractionDigits(cdbl(strCal)));
        if (vbLen(strDot) > 0) strCal = strCal + '.' + strDot;
      }
    }

    if (intSF > 1 && Math.abs(cdbl(strCal)) < 1) {
      while (!(vbLeft(vbRight(strCal, intSF), 1) !== '0' && vbLeft(vbRight(strCal, intSF), 1) !== '.')) strCal = strCal + '0';
    }

    if (intCorrPower !== 0) {
      let strFirst = vbLeft(strCal, 1); if (strFirst === '-') strFirst = vbLeft(strCal, 2);
      const strSecond = vbRight(strCal, strCal.length - 1);
      strResult = vbLen(strSecond) > 0
        ? strFirst + '.' + strSecond.replace(/\./g, '') + ' X 10 ' + superScript(cstr(intCorrPower))
        : strFirst + ' X 10 ' + superScript(cstr(intCorrPower));
      return strResult;
    } else {
      let strDot = '';
      if ((intSF - (intInputDigitCount + 1)) >= 0 && (intSF - (intInputDigitCount + 1)) <= strCal.length) {
        if (cdbl(mid(strCal, strCal.length - (intSF - (intInputDigitCount + 1)), strCal.length)) === 0)
          strDot = mid(strCal, strCal.length - (intSF - (intInputDigitCount + 1)), strCal.length);
      }
      strCal = cstr(cdbl(strCal) * 10 ** (intCorrPower - (intSF - 1)));
      if (vbLen(strDot) > 0) strCal = strCal + '.' + strDot;
      return strCal;
    }
  } catch {
    return cstr(dblInput);
  }
}
